/**
 * API Cart Service - Transport layer for cart operations
 *
 * Pure transport layer with no UI logic or business logic.
 * Implements cart adding functionality compatible with both local and Bitrix environments.
 */

import { CartAddRequest, CartAddResponse, ApiCartServiceConfig } from "../types/api";
import { isBitrixMode, buildCartAddUrl } from "../utils/bitrixApiUrl";

// Default configuration
const DEFAULT_CONFIG: ApiCartServiceConfig = {
	baseUrl: import.meta.env.VITE_API_BASE_URL || "/local/modules/live_search/api",
	timeout: 10000, // 10 seconds
	headers: {
		"Content-Type": "application/json",
	},
};

/**
 * API Cart Service implementation
 *
 * This service is a pure transport layer that:
 * - Sends POST requests to /api/cart/add
 * - Handles errors and timeouts
 * - Returns response from server
 */
export class ApiCartService {
	private config: ApiCartServiceConfig;
	private abortController: AbortController | null = null;

	constructor(config: Partial<ApiCartServiceConfig> = {}) {
		this.config = { ...DEFAULT_CONFIG, ...config };
	}

	/**
	 * Add product to cart
	 *
	 * @param productId - Product ID
	 * @param quantity - Quantity (default 1)
	 * @returns CartAddResponse
	 */
	async addToCart(productId: number, quantity: number = 1): Promise<CartAddResponse> {
		// Cancel any ongoing request
		this.cancelCurrentRequest();

		// Create new abort controller for this request
		this.abortController = new AbortController();

		try {
			// Prepare request body
			const requestBody: CartAddRequest = {
				id: productId,
				quantity: quantity,
			};

			// Prepare fetch options
			const timeout = this.config.timeout || DEFAULT_CONFIG.timeout;
			const controller = this.abortController;

			// Set up timeout promise
			const timeoutPromise = new Promise<never>((_, reject) => {
				setTimeout(() => {
					controller.abort();
					reject(new Error(`Request timeout after ${timeout}ms`));
				}, timeout);
			});

			// Prepare fetch request
			const url = isBitrixMode() ? buildCartAddUrl() : `${this.config.baseUrl}/api/cart/add`;
			const fetchPromise = fetch(url, {
				method: "POST",
				headers: {
					...this.config.headers,
					Accept: "application/json",
				},
				body: JSON.stringify(requestBody),
				signal: controller.signal,
			});

			// Race between fetch and timeout
			const response = await Promise.race([fetchPromise, timeoutPromise]);

			if (!response.ok) {
				let errorMessage = `HTTP error ${response.status}`;
				try {
					const errorData = await response.json();
					errorMessage = errorData.error || errorData.message || errorMessage;
				} catch {
					// Ignore JSON parsing errors
				}
				throw new Error(errorMessage);
			}

			// Parse response
			const data: CartAddResponse = await response.json();
			return data;
		} catch (error) {
			// Handle abort errors separately
			if (error instanceof Error && error.name === "AbortError") {
				throw new Error("Cart request was cancelled");
			}

			// Re-throw the error for the caller to handle
			throw error;
		} finally {
			// Clear the abort controller
			this.abortController = null;
		}
	}

	/**
	 * Cancel the current cart request if any
	 */
	private cancelCurrentRequest(): void {
		if (this.abortController) {
			this.abortController.abort();
			this.abortController = null;
		}
	}

	/**
	 * Cancel any ongoing cart request
	 * Public method for the hook to call
	 */
	cancel(): void {
		this.cancelCurrentRequest();
	}
}